import React, { useEffect, useState, useMemo, useRef } from 'react';
import { InputWithCopyButton } from 'components/calls/molecules/Info';
import DeviceSettings from './device-settings';
import MediaContent from './media-content';
import RoomCreated, { RoomInfo } from '../room-created';
import { useSbCalls } from '../../lib/sendbird-calls';
import Header from '../header';
import { AuthOption } from 'sendbird-calls';
import { IconLoading } from 'components/common/icons';
import { PermissionModal } from '../modals/permission-modal';
import {
  getSendbirdUser,
  getSendbirdRoom,
  createSendbirdUser,
  editVideoAssignmentSubmissionRoomId,
  fetchByUserIdAndVideoAssignmentId,
  fetchUser,
  startCopyStepFunctions,
  uploadToZapier,
} from 'api';
import { useRouter } from 'next/router';
import { ErrorMessage, SuccessMessage } from 'components/calls/atoms/Toast';
import { toast } from 'react-toastify';
import {
  Button,
  ButtonIcon,
  CenterDiv,
  Content,
  Footer,
  LoaderContainer,
  Main,
  Participants,
  ShareWrapper,
  Side,
  SideClose,
  SideTitle,
  UtilityButtons,
} from './styles';
import { Participant } from './participants';
import {
  EndButtonIcon,
  EndButton,
  MuteButton,
  StartVideoButton,
  StopVideoButton,
  UnmuteButton,
} from 'components/calls/atoms/CallButtons';
import { getFormattedDate } from 'components/calls/utils';
import {
  RECORDING_STARTED_AT_KEY,
  RECORDING_STOPPED_AT_KEY,
  getRecordingStateFromCustomItems,
} from 'components/calls/utils/recording';

interface GroupCallMainProps {
  submissionId: string;
  assignmentId: string;
  classId?: string;
  userId: string;
  firstCreate: string;
  maxLengthInSeconds: string;
}

export const ROOM_ID_KEY = 'room_id';

const beforeUnload = (event) => {
  event.preventDefault();
  event.returnValue = 'Your upload is still in progress. Do you want to leave this page?';
};

export const GroupCall = ({
  assignmentId,
  submissionId,
  classId,
  userId,
  firstCreate,
  maxLengthInSeconds,
}: GroupCallMainProps) => {
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [showSide, setShowSide] = useState(false);
  const [showRoomInfo, setShowRoomInfo] = useState(false);
  const sbCalls = useSbCalls();
  const { rooms } = sbCalls;
  const [loading, setIsLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [showRoomCreated, setShowRoomCreated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null);
  const [recordingStoppedAt, setRecordingStoppedAt] = useState<number | null>(null);

  const isRecording = recordingStartedAt != null && recordingStoppedAt == null;
  const recordingFinished = recordingStartedAt != null && recordingStoppedAt != null;

  const router = useRouter();
  const sideRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const room = useMemo(() => {
    return rooms.find((r) => !!r.localParticipant);
  }, [rooms]);

  const { participants, localParticipant } = room || {};

  const onGetSendbirdUser = async (userId: string): Promise<string> => {
    try {
      const result = await getSendbirdUser(userId);

      if (!result) {
        return null;
      }

      return result.user_id;
    } catch (err) {
      return null;
    }
  };

  const onCreateSendbirdUser = async (id: string, userName: string): Promise<string> => {
    const result = await createSendbirdUser({
      user_id: id,
      nickname: userName || 'No username',
      profile_url: '/',
    });
    return result.user_id;
  };

  const prepareSendbirdUser = async () => {
    try {
      const userData = await fetchUser();
      setCurrentUser(userData);
      const currentUserId = userData?.userId || userId;

      if (currentUserId) {
        const sbUser = await onGetSendbirdUser(currentUserId);
        if (sbUser) {
          return sbUser;
        }

        const createdUser = await onCreateSendbirdUser(
          currentUserId,
          userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : userData.username || userData.emailAddress
        );
        return createdUser;
      }
    } catch (error) {
      console.error(error);
      toast.error(
        <ErrorMessage message="Unfortunately, we encountered a problem. Please navigate back and try to join the room again." />,
        {
          autoClose: 3000,
        }
      );
    }
  };

  const createRoom = async () => {
    try {
      const room = await sbCalls.createRoom({
        roomType: sbCalls.RoomType.SMALL_ROOM_FOR_VIDEO,
      });
      return room;
    } catch (e) {
      toast.error(<ErrorMessage message={e.message} />, { autoClose: 2000 });
    }
  };

  const enter = async (id: string, skipDialog: boolean) => {
    try {
      const room = await sbCalls.fetchRoomById(id);

      if (!room || room.state === 'deleted') {
        console.error('Cannot enter deleted room', id);
        toast.error(
          <ErrorMessage message="Unfortunately, we encountered a problem. Please navigate back and try to join the room again." />,
          {
            autoClose: 3000,
          }
        );
        if (typeof window !== 'undefined') setTimeout(() => window.history.back(), 2900);
        return null;
      }

      if (skipDialog) {
        await room.enter({
          audioEnabled: true,
          videoEnabled: true,
          kickSiblings: true,
        });
      }

      return room;
    } catch (e) {
      console.error(e);
    }
  };

  const login = async (userId: string) => {
    const APP_ID = process.env.NEXT_PUBLIC_SENDBIRD_APP_APP_ID || '';
    const accessToken = process.env.NEXT_PUBLIC_SENDBIRD_APP_ACCESS_TOKEN || '';

    const option: AuthOption = { userId, accessToken };

    sbCalls.init(APP_ID);

    try {
      await sbCalls.auth(option);

      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const exitAndDeleteRoom = async () => {
    try {
      await room.exit();
      if (room.state !== 'deleted') await room.delete();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCallExit = async () => {
    window.removeEventListener('beforeunload', beforeUnload);
    if (loadingAction) {
      return;
    }
    setLoadingAction(true);
    try {
      await editVideoAssignmentSubmissionRoomId(submissionId, undefined);
    } catch (e) {
      console.error(e);
    }
    // Always exit + delete the Sendbird room, even if the submission update above
    // failed — otherwise the room keeps running on Sendbird's side and leaks media.
    await exitAndDeleteRoom();
    setLoadingAction(false);
    router.push(sessionStorage.getItem('referrer') || '/user/classes', undefined);
    sessionStorage.removeItem('referrer');
  };

  const handleStartRecording = () => {
    if (loadingAction) return;
    if (!room.remoteParticipants.length) {
      toast.error(<ErrorMessage message="Cannot start recording without a guest!" />, {
        autoClose: 2000,
        position: 'top-right',
      });
      return;
    }
    const startedAt = Date.now();
    setRecordingStartedAt(startedAt);
    setRecordingStoppedAt(null);
    // Broadcast to the guest in real time via Sendbird room custom items. Each
    // session uses a fresh room, so there's no stale stopped value to clear —
    // we only write the start key (Sendbird rejects empty-string values).
    room
      .updateCustomItems({ [RECORDING_STARTED_AT_KEY]: String(startedAt) })
      .catch((e) => console.error(e));
  };

  const handleStopRecording = () => {
    if (loadingAction) return;
    const stoppedAt = Date.now();
    setRecordingStoppedAt(stoppedAt);
    room
      .updateCustomItems({ [RECORDING_STOPPED_AT_KEY]: String(stoppedAt) })
      .catch((e) => console.error(e));
  };

  const handleStartCopyStepFunctions = async (params, retries = 3) => {
    try {
      await startCopyStepFunctions(params);
    } catch (err) {
      if (retries > 1) {
        await handleStartCopyStepFunctions(params, retries - 1);
      } else {
        throw {
          ...err,
          name: 'CopyStepFunctionsError',
        };
      }
    }
  };

  const handleSaveRecording = async () => {
    const PUBLIC_SENDBIRD_APP_ID = process.env.NEXT_PUBLIC_SENDBIRD_APP_APP_ID;

    window.removeEventListener('beforeunload', beforeUnload);
    if (loadingAction) {
      return;
    }
    if (recordingStartedAt == null || recordingStoppedAt == null) {
      toast.error(
        <ErrorMessage message="Please start and stop the recording before saving." />,
        { autoClose: 3000 }
      );
      return;
    }
    setLoadingAction(true);
    try {
      const sendbirdRoom = await getSendbirdRoom(room.roomId);

      if (!sendbirdRoom.cloud_recordings?.length) {
        console.log('No cloud recordings found!');
        toast.error(
          <ErrorMessage message="No recording was captured for this session." />,
          { autoClose: 5000 }
        );
        return;
      }

      const recordingStartMs = sendbirdRoom.cloud_recordings[0]?.started_at;
      if (recordingStartMs == null) {
        toast.error(
          <ErrorMessage message="Recording timing data is unavailable. Please try again in a moment." />,
          { autoClose: 5000 }
        );
        return;
      }

      localStorage.setItem(ROOM_ID_KEY, room.roomId);

      const stepFunctionParams = {
        classId,
        submissionId,
        userId: room.createdBy,
        recordingRoomId: room.roomId,
        cloudRecordings: sendbirdRoom.cloud_recordings.map(
          (item: { recording_id: string }) => item.recording_id
        ),
        startedAt: Math.max(0, recordingStartedAt - recordingStartMs),
        stoppedAt: Math.max(0, recordingStoppedAt - recordingStartMs),
      };

      await handleStartCopyStepFunctions(stepFunctionParams);

      toast(
        <SuccessMessage message="Recording is being processed, it will be available in the psa section in a few minutes" />,
        { autoClose: 5000, position: 'top-right' }
      );

      await editVideoAssignmentSubmissionRoomId(submissionId, undefined);
      await exitAndDeleteRoom();
      router.push(sessionStorage.getItem('referrer') || '/user/classes', undefined);
      sessionStorage.removeItem('referrer');
    } catch (e) {
      console.error('Error in handleSaveRecording:', e);
      if (e.name === 'CopyStepFunctionsError') {
        const sendbirdRoomLink = room.roomId
          ? `https://dashboard.sendbird.com/${PUBLIC_SENDBIRD_APP_ID}/calls/group-calls/${room.roomId}`
          : undefined;
        try {
          await uploadToZapier({
            user_id: String(currentUser.userId),
            user_email: currentUser.emailAddress,
            message: `Recording processing failed. Assistance needed. (PSA ID: ${assignmentId})`,
            date: getFormattedDate(new Date().toISOString()),
            issue_type: 'Call',
            room: sendbirdRoomLink,
          });

          toast.error(
            <ErrorMessage message="Unfortunately, we encountered a problem processing your recording. Our support team has been informed and will get in touch as soon as the issue is fixed." />,
            { autoClose: 10000 }
          );
        } catch (zapierError) {
          // Only claim support was informed when the Zapier upload actually went through
          console.error('Failed to notify support about the recording failure:', zapierError);
          toast.error(
            <ErrorMessage message="Unfortunately, we encountered a problem processing your recording. Please contact support." />,
            { autoClose: 10000 }
          );
        }
      } else {
        toast.error(
          <ErrorMessage message="Unfortunately, we encountered a problem. Please try again" />,
          { autoClose: 10000 }
        );
      }
    } finally {
      setLoadingAction(false);
    }
  };

  useEffect(() => {
    if (!userId) {
      return;
    }

    if (!assignmentId) {
      return;
    }

    window.addEventListener('beforeunload', beforeUnload);

    setIsLoading(true);
    const sendbirdInit = async () => {
      const preppedId = await prepareSendbirdUser();
      try {
        await login(preppedId);

        if (firstCreate) {
          const room = await createRoom();
          if (room) {
            await editVideoAssignmentSubmissionRoomId(submissionId, room.roomId);
            setShowRoomCreated(true);
            await enter(room.roomId, true);
          }
          delete router.query.firstCreate;
          return router.push(router);
        }

        const result = await fetchByUserIdAndVideoAssignmentId(userId, assignmentId);

        // Restore recording state for refresh-during-call.
        setRecordingStartedAt(result?.recordingStartedAt ?? null);
        setRecordingStoppedAt(result?.recordingStoppedAt ?? null);

        if (result.roomId) {
          const enteredRoom = await enter(result.roomId, true);
          if (enteredRoom) {
            // The live recording lifecycle is broadcast on the room's custom
            // items (the submission fields aren't persisted until Save), so on a
            // refresh mid-recording prefer them — this restores the host's
            // indicator + Stop/Save controls and keeps host and guest in sync.
            const { startedAt, stoppedAt } = getRecordingStateFromCustomItems(
              enteredRoom.customItems
            );
            if (startedAt !== undefined) {
              setRecordingStartedAt(startedAt);
              setRecordingStoppedAt(stoppedAt ?? null);
            }
            return;
          }
        }
      } catch (error) {
        console.error(error);
        toast.error(
          <ErrorMessage message="Unfortunately, we encountered a problem. Please navigate back and try to join the room again." />,
          {
            autoClose: 5000,
          }
        );
      }
    };

    sendbirdInit();
    setIsLoading(false);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [userId, assignmentId]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        sideRef.current &&
        !sideRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowSide(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!room || loading || !localParticipant) {
    return (
      <LoaderContainer>
        <IconLoading />
      </LoaderContainer>
    );
  }

  return (
    <Content>
      <Header
        showRecording={isRecording}
        maxLengthInSeconds={maxLengthInSeconds}
        recordingStartEpoch={recordingStartedAt ?? undefined}
        roomId={room.roomId ? room.roomId : undefined}
        userId={userId}
      />

      <Main show={showSide} recording={isRecording && !loadingAction}>
        {room.createdBy === room.localParticipant.user.userId && (
          <RoomCreated
            isOpen={showRoomCreated}
            close={() => setShowRoomCreated(false)}
            room={room}
            userId={userId}
            assignmentId={assignmentId}
          />
        )}
        {loadingAction ? (
          <CenterDiv>
            <IconLoading color="#ffff" />
          </CenterDiv>
        ) : (
          <MediaContent room={room} showParticipants={showSide} />
        )}
        <PermissionModal />

        <Footer>
          <Button onClick={() => setShowDeviceSettings(true)}>
            <ButtonIcon src="/icons/ic-settings.svg" />
            Settings
          </Button>
          <UtilityButtons>
            {localParticipant.isAudioEnabled ? (
              <MuteButton onClick={() => localParticipant.muteMicrophone()} />
            ) : (
              <UnmuteButton onClick={() => localParticipant.unmuteMicrophone()} />
            )}
            {localParticipant.isVideoEnabled ? (
              <StopVideoButton onClick={() => localParticipant.stopVideo()} />
            ) : (
              <StartVideoButton onClick={() => localParticipant.startVideo()} />
            )}
            {room.createdBy === room.localParticipant.user.userId && (
              <>
                {recordingFinished ? (
                  <EndButton onClick={handleSaveRecording} disabled={loadingAction}>
                    {loadingAction ? <IconLoading color="#ffff" width={16} /> : 'Save Recording'}
                  </EndButton>
                ) : (
                  <>
                    <EndButtonIcon onClick={handleCallExit} disabled={loadingAction} />
                    {isRecording ? (
                      <EndButton onClick={handleStopRecording} disabled={loadingAction}>
                        {loadingAction ? (
                          <IconLoading color="#ffff" width={16} />
                        ) : (
                          'Stop Recording'
                        )}
                      </EndButton>
                    ) : (
                      <EndButton onClick={handleStartRecording} disabled={loadingAction}>
                        {loadingAction ? (
                          <IconLoading color="#ffff" width={16} />
                        ) : (
                          'Start Recording'
                        )}
                      </EndButton>
                    )}
                  </>
                )}
              </>
            )}
          </UtilityButtons>
          <Button ref={buttonRef} onClick={() => showSide === false && setShowSide(true)}>
            <ButtonIcon src="/icons/ic-user.svg" />
            Share
          </Button>
        </Footer>
        <DeviceSettings isOpen={showDeviceSettings} close={() => setShowDeviceSettings(false)} />
        <RoomInfo isOpen={showRoomInfo} room={room} close={() => setShowRoomInfo(false)} />
      </Main>

      <Side show={showSide} ref={sideRef}>
        <SideTitle>
          <div>Participants ({room.participants.length})</div>
          <SideClose onClick={() => setShowSide(false)}>
            <div />
          </SideClose>
        </SideTitle>
        <Participants>
          {participants?.map((p) => (
            <Participant participant={p} key={p.participantId} />
          ))}
        </Participants>
        <ShareWrapper>
          <InputWithCopyButton
            title="Share room url"
            text={`${
              process.env.NODE_ENV === 'development'
                ? 'http://localhost:3000'
                : `https://${window.location.hostname}`
            }/user/classes/calls/join/${userId}/${assignmentId}`}
          />
        </ShareWrapper>
      </Side>
    </Content>
  );
};
